function debounce(fn, wait = 300) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.slides = Array.from(this.querySelectorAll('.announcement__text'));
      this.previousButton = this.querySelector('[data-announcement-previous]');
      this.nextButton = this.querySelector('[data-announcement-next]');
      this.toggleButton = this.querySelector('[data-announcement-toggle]');
      this.statusElement = this.querySelector('[data-announcement-status]');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.currentIndex = 0;
      this.timer = null;
      this.pointerPaused = false;
      this.focusPaused = false;
      this.userPaused = false;
      this.preferencePaused = this.reducedMotion.matches;
      this.moveLinksHandler = this.moveLinks.bind(this);
      this.moveLocalizationHandler = debounce(this.moveLocalization.bind(this));
      this.pauseForPointer = this.pauseForPointer.bind(this);
      this.resumeAfterPointer = this.resumeAfterPointer.bind(this);
      this.pauseForFocus = this.pauseForFocus.bind(this);
      this.resumeAfterFocus = this.resumeAfterFocus.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleToggle = this.handleToggle.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionPreference = this.handleMotionPreference.bind(this);
      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.reducedMotion.removeEventListener('change', this.handleMotionPreference);
      this.slider?.removeEventListener('mouseenter', this.pauseForPointer);
      this.slider?.removeEventListener('mouseleave', this.resumeAfterPointer);
      this.removeEventListener('focusin', this.pauseForFocus);
      this.removeEventListener('focusout', this.resumeAfterFocus);
      this.slider?.removeEventListener('keydown', this.handleKeydown);
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.toggleButton?.removeEventListener('click', this.handleToggle);
    }

    bindEvents() {
      if (this.links.length) {
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (!this.slider) return;

      this.slider.addEventListener('mouseenter', this.pauseForPointer);
      this.slider.addEventListener('mouseleave', this.resumeAfterPointer);
      this.addEventListener('focusin', this.pauseForFocus);
      this.addEventListener('focusout', this.resumeAfterFocus);
      this.slider.addEventListener('keydown', this.handleKeydown);
      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.toggleButton?.addEventListener('click', this.handleToggle);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.reducedMotion.addEventListener('change', this.handleMotionPreference);
    }

    init() {
      if (this.slider && this.slides.length > 1) {
        this.showSlide(0, false, true);
        this.updateToggleButton();
        if (this.toggleButton) this.scheduleNext();
      }

      if (this.links.length) this.moveLinks();
      if (this.localization) this.moveLocalization();
    }

    scheduleNext() {
      clearTimeout(this.timer);
      if (!this.toggleButton || this.shouldPausePermanently() || this.isInteractionPaused() || document.hidden) return;

      const delay = Math.max(Number(this.dataset.slideDelay) || 7000, 1000);
      this.timer = setTimeout(() => {
        if (this.isInViewport() && !this.isInteractionPaused()) {
          this.showSlide(this.currentIndex + 1, false);
        }
        this.scheduleNext();
      }, delay);
    }

    shouldPausePermanently() {
      return this.userPaused || this.preferencePaused;
    }

    isInteractionPaused() {
      return this.pointerPaused || this.focusPaused;
    }

    isInViewport() {
      if (theme.elementUtil?.isInViewport) return theme.elementUtil.isInViewport(this);
      const rect = this.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    showSlide(index, announce = false, instant = false) {
      const total = this.slides.length;
      this.currentIndex = (index + total) % total;
      const activeSlide = this.slides[this.currentIndex];

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.currentIndex;
        slide.classList.toggle('is-visible', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.inert = !isActive;
      });

      const left = document.dir === 'rtl'
        ? -Math.abs(activeSlide.offsetLeft)
        : activeSlide.offsetLeft;
      this.slider.scrollTo({
        left,
        behavior: instant || this.reducedMotion.matches ? 'auto' : 'smooth'
      });

      if (announce && this.statusElement) {
        const template = this.slider.dataset.statusTemplate;
        this.statusElement.textContent = template
          .replace('__CURRENT__', String(this.currentIndex + 1))
          .replace('__TOTAL__', String(total));
      }
    }

    handlePrevious() {
      this.showSlide(this.currentIndex - 1, true);
      this.scheduleNext();
    }

    handleNext() {
      this.showSlide(this.currentIndex + 1, true);
      this.scheduleNext();
    }

    handleToggle() {
      if (this.preferencePaused) {
        this.preferencePaused = false;
        this.userPaused = false;
      } else {
        this.userPaused = !this.userPaused;
      }
      this.updateToggleButton();
      this.scheduleNext();
    }

    updateToggleButton() {
      if (!this.toggleButton) return;
      const paused = this.shouldPausePermanently();
      this.toggleButton.dataset.state = paused ? 'paused' : 'playing';
      this.toggleButton.setAttribute('aria-pressed', String(paused));
      this.toggleButton.setAttribute(
        'aria-label',
        paused ? this.toggleButton.dataset.playLabel : this.toggleButton.dataset.pauseLabel
      );
    }

    handleKeydown(event) {
      if (event.target !== this.slider) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNext();
      }
    }

    pauseForPointer() {
      this.pointerPaused = true;
      clearTimeout(this.timer);
    }

    resumeAfterPointer() {
      this.pointerPaused = false;
      this.scheduleNext();
    }

    pauseForFocus() {
      this.focusPaused = true;
      clearTimeout(this.timer);
    }

    resumeAfterFocus(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.focusPaused = false;
      this.scheduleNext();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        clearTimeout(this.timer);
      } else {
        this.scheduleNext();
      }
    }

    handleMotionPreference(event) {
      this.preferencePaused = event.matches;
      this.updateToggleButton();
      this.scheduleNext();
    }

    moveLinks() {
      const mobileLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !mobileLinks && this.menu) {
        const navigation = document.createElement('nav');
        navigation.className = 'mob__announcement-links';
        navigation.setAttribute('aria-label', this.dataset.secondaryNavLabel);
        const list = document.createElement('ul');
        list.className = 'secondary-nav';

        this.links.forEach((link) => {
          const item = document.createElement('li');
          const mobileLink = document.createElement('a');
          mobileLink.className = 'secondary-nav__item';
          mobileLink.href = link.href;
          mobileLink.textContent = link.textContent.trim();
          item.appendChild(mobileLink);
          list.appendChild(item);
        });

        navigation.appendChild(list);
        this.menu.appendChild(navigation);
      } else if (theme.mediaMatches.md && mobileLinks) {
        mobileLinks.remove();
      }
    }

    moveLocalization() {
      const mobileLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !mobileLocalization && this.menu) {
        const mobileContainer = document.createElement('div');
        mobileContainer.className = 'mob__localization';
        if (this.localization.firstElementChild) {
          mobileContainer.appendChild(this.localization.firstElementChild);
          this.menu.appendChild(mobileContainer);
        }
      } else if (theme.mediaMatches.md && mobileLocalization) {
        if (mobileLocalization.firstElementChild) {
          this.localization.appendChild(mobileLocalization.firstElementChild);
        }
        mobileLocalization.remove();
      }
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
